import { CrumpetCorner } from '../../model/components/CrumpetCorner';
import {
    getDefaultPollByType,
    VoteSliderArticlePage,
} from '../../model/components/PollArticlePage';
import { isVoteSliderPost } from '../../model/PostTypes';
import { VoteSliderPostData } from '../../model/types/VoteSliderPostData';
import { PathContextProvider } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { CrumpetCornerInput } from './CrumpetCornerInput';
import { MediaInput } from './MediaInput';
import { PollVoteSliderInput } from './PollVoteSliderInput';
import { TextTypeInput } from './TextTypeInput';
import { VoteSliderPollData } from '@lifeserver/shared';

import { assert } from '../../../utils/assert';

import styles from './VoteSliderPostInput.module.scss';

export interface VoteSliderPostInputProps {
    value: VoteSliderPostData;
    onChange?: (value: VoteSliderPostData) => void;
}

export const VoteSliderPostInput = ({
    value,
    onChange,
}: VoteSliderPostInputProps) => {
    const { post, validations, isEditingExistingPost, initialPostState } =
        usePortalPostEditor();

    const canModifyPolls =
        !isEditingExistingPost || initialPostState !== 'ready';

    assert(
        isVoteSliderPost(post),
        'VoteSliderPostInput must be rendered with a VoteSlider post',
    );

    const [frontPage, backPage] = value.pages;

    const onFrontPageChange = (page: VoteSliderArticlePage) => {
        onChange?.({
            ...value,
            pages: [page, ...(backPage ? [backPage] : [])],
        });
    };

    const onBackPageChange = (page: VoteSliderArticlePage) => {
        onChange?.({
            ...value,
            pages: [frontPage, page],
        });
    };

    const onCrumpetCornerChange = (crumpetCorner?: CrumpetCorner) => {
        onFrontPageChange({
            ...frontPage,
            crumpetCorner,
        });
    };

    const createDefaultPoll = (): VoteSliderPollData =>
        getDefaultPollByType('VoteSlider');

    const toggleFrontPagePoll = (enabled: boolean) => {
        onFrontPageChange({
            ...frontPage,
            poll: enabled ? createDefaultPoll() : undefined,
        });
    };

    const toggleBackPagePoll = (enabled: boolean) => {
        onBackPageChange({
            ...backPage,
            poll: enabled ? createDefaultPoll() : undefined,
        });
    };

    const toggleBackPage = (enabled: boolean) => {
        if (enabled && !backPage) {
            // Add a new back page
            const newBackPage: VoteSliderArticlePage = {
                background: undefined,
                header: undefined,
                body: undefined,
                popTexts: undefined,
                crumpetCorner: undefined,
                poll: undefined,
            };
            onChange?.({
                ...value,
                pages: [frontPage, newBackPage],
            });
        } else if (!enabled && backPage) {
            const updatedFrontPage: VoteSliderArticlePage = {
                ...frontPage,
                crumpetCorner: undefined,
            };
            onChange?.({
                ...value,
                pages: [updatedFrontPage],
            });
        }
    };

    // Validation: at least one page must have a poll
    const hasPoll = frontPage.poll || backPage?.poll;
    const hasValidationError = !hasPoll;

    return (
        <div>
            {hasValidationError && (
                <div className={styles.validationError}>
                    At least one page must have a poll configured.
                </div>
            )}

            <div className={styles.container}>
                <fieldset>
                    <legend>Front Page</legend>

                    <fieldset>
                        <legend>Background</legend>
                        <MediaInput
                            value={frontPage.background}
                            onChange={(background) =>
                                onFrontPageChange({
                                    ...frontPage,
                                    background,
                                })
                            }
                        />
                    </fieldset>

                    {backPage && (
                        <fieldset>
                            <legend>Crumpet Corner</legend>
                            <CrumpetCornerInput
                                value={frontPage.crumpetCorner}
                                onChange={onCrumpetCornerChange}
                            />
                        </fieldset>
                    )}
                    <PathContextProvider segment="pages">
                        <PathContextProvider segment={0}>
                            <TextTypeInput
                                value={frontPage}
                                onChange={(updatedPage) => {
                                    const voteSliderPage: VoteSliderArticlePage =
                                        {
                                            ...updatedPage,
                                            poll: frontPage.poll,
                                        };
                                    onFrontPageChange(voteSliderPage);
                                }}
                                pageIndex={0}
                                validations={validations}
                            />
                        </PathContextProvider>
                    </PathContextProvider>

                    {(canModifyPolls || frontPage.poll) && (
                        <fieldset>
                            <legend>Poll</legend>
                            {canModifyPolls && (
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(frontPage.poll)}
                                        onChange={(e) =>
                                            toggleFrontPagePoll(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    Include poll on front page
                                </label>
                            )}

                            {frontPage.poll && (
                                <PollVoteSliderInput
                                    value={frontPage.poll}
                                    onChange={(poll) =>
                                        onFrontPageChange({
                                            ...frontPage,
                                            poll: poll,
                                        })
                                    }
                                />
                            )}
                        </fieldset>
                    )}
                </fieldset>

                <fieldset>
                    <legend>Back Page</legend>
                    <label>
                        <input
                            type="checkbox"
                            checked={Boolean(backPage)}
                            onChange={(e) => toggleBackPage(e.target.checked)}
                        />
                        Include back page
                    </label>

                    {backPage && (
                        <>
                            <fieldset>
                                <legend>Background</legend>
                                <MediaInput
                                    value={backPage.background}
                                    onChange={(background) =>
                                        onBackPageChange({
                                            ...backPage,
                                            background,
                                        })
                                    }
                                />
                            </fieldset>
                            <PathContextProvider segment="pages">
                                <PathContextProvider segment={1}>
                                    <TextTypeInput
                                        value={backPage}
                                        onChange={(updatedPage) => {
                                            const voteSliderBackPage: VoteSliderArticlePage =
                                                {
                                                    ...updatedPage,
                                                    poll: backPage.poll,
                                                };
                                            onBackPageChange(
                                                voteSliderBackPage,
                                            );
                                        }}
                                        pageIndex={1}
                                        validations={validations}
                                    />
                                </PathContextProvider>
                            </PathContextProvider>

                            {(canModifyPolls || backPage.poll) && (
                                <fieldset>
                                    <legend>Poll</legend>
                                    {canModifyPolls && (
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(backPage.poll)}
                                                onChange={(e) =>
                                                    toggleBackPagePoll(
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                            Include poll on back page
                                        </label>
                                    )}

                                    {backPage.poll && (
                                        <PollVoteSliderInput
                                            value={backPage.poll}
                                            onChange={(poll) =>
                                                onBackPageChange({
                                                    ...backPage,
                                                    poll: poll,
                                                })
                                            }
                                        />
                                    )}
                                </fieldset>
                            )}
                        </>
                    )}
                </fieldset>
            </div>
        </div>
    );
};
